import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { PlusIcon, XIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { CompanyProfileSectionTitle } from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { useSuccessToast } from "@/hooks";
import {
  useCreateCompanyInformation,
  useUpdateCompanyInformation,
} from "@/hooks/mutations";
import { useSetCompanyInformation } from "@/hooks/sets";
import {
  Company,
  CompanyInformation,
  CreateOrUpdateCompanyInformationRequest,
} from "@/types";

const companyInformationSchema = z.object({
  address: z.string(),
  description: z.string(),
  naicsCodes: z.array(z.string()),
  customerIndustries: z.array(z.string()),
  manufacturingCapabilities: z.array(z.string()),
  equipment: z.array(z.string()),
  materials: z.array(z.string()),
  headcount: z.number().nullable(), // TODO: Figure out the best way to handle number inputs.
  foundedYear: z.string(),
  location: z.string(),
  dunsNumber: z.string(),
  cageCode: z.string(),
});

const ArrayInput = ({
  field,
  placeholder,
}: {
  field: {
    onChange: (value: string[]) => void;
    value: string[];
  };
  placeholder: string;
}) => {
  const [currentValue, setCurrentValue] = useState("");

  const handleAdd = () => {
    if (currentValue.trim()) {
      field.onChange([...field.value, currentValue.trim()]);
      setCurrentValue("");
    }
  };

  const handleRemove = (index: number) => {
    field.onChange(field.value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        <Input
          onChange={(e) => {
            setCurrentValue(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          value={currentValue}
        />
        <Button onClick={handleAdd} type="button" variant="outline">
          <PlusIcon className="h-4 w-4" />
          Add
        </Button>
      </div>
      {field.value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {field.value.map((item, index) => (
            <Badge
              className="gap-1 border-primary-900 bg-primary-400"
              key={index}
            >
              {item}
              <XIcon
                className="h-4 w-4 cursor-pointer"
                onClick={() => {
                  handleRemove(index);
                }}
              />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export const CompanyProfileInformationForm = ({
  company,
  companyInformation,
  setIsEditing,
}: {
  company: Company;
  companyInformation: CompanyInformation | null;
  setIsEditing: (isEditing: boolean) => void;
}) => {
  const { mutate: createCompanyInformation } = useCreateCompanyInformation();
  const { mutate: updateCompanyInformation } = useUpdateCompanyInformation();
  const setCompanyInformation = useSetCompanyInformation();
  const successToast = useSuccessToast();

  const form = useForm({
    defaultValues: {
      address: companyInformation?.companyInformation.address ?? "",
      description: companyInformation?.companyInformation.description ?? "",
      naicsCodes: companyInformation?.companyInformation.naicsCodes ?? [],
      customerIndustries:
        companyInformation?.companyInformation.customerIndustries ?? [],
      manufacturingCapabilities:
        companyInformation?.companyInformation.manufacturingCapabilities ?? [],
      equipment: companyInformation?.companyInformation.equipment ?? [],
      materials: companyInformation?.companyInformation.materials ?? [],
      headcount: companyInformation?.companyInformation.headcount ?? null,
      foundedYear: companyInformation?.companyInformation.foundedYear ?? "",
      location: companyInformation?.companyInformation.location ?? "",
      dunsNumber: companyInformation?.companyInformation.dunsNumber ?? "",
      cageCode: companyInformation?.companyInformation.cageCode ?? "",
    },
    resolver: zodResolver(companyInformationSchema),
  });

  const handleSubmit = (data: z.infer<typeof companyInformationSchema>) => {
    const createOrUpdateCompanyInformationRequest: CreateOrUpdateCompanyInformationRequest =
      {
        companyInformation: {
          address: data.address || null,
          description: data.description || null,
          naicsCodes: data.naicsCodes,
          customerIndustries: data.customerIndustries,
          manufacturingCapabilities: data.manufacturingCapabilities,
          equipment: data.equipment,
          materials: data.materials,
          headcount: data.headcount,
          foundedYear: data.foundedYear || null,
          location: data.location || null,
          dunsNumber: data.dunsNumber || null,
          cageCode: data.cageCode || null,
        },
      };

    if (companyInformation) {
      updateCompanyInformation(
        {
          companyInformationId: companyInformation.id,
          updateCompanyInformationRequest:
            createOrUpdateCompanyInformationRequest,
        },
        {
          onSuccess: (data) => {
            handleSuccess(data);
          },
        },
      );
    } else {
      createCompanyInformation(
        {
          createCompanyInformationRequest:
            createOrUpdateCompanyInformationRequest,
        },
        {
          onSuccess: (data) => {
            handleSuccess(data);
          },
        },
      );
    }
  };

  const handleSuccess = (companyInformation: CompanyInformation) => {
    setIsEditing(false);
    setCompanyInformation({
      companyInformation,
    });
    successToast({
      description: "Company information updated successfully!",
    });
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-8"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        {/* Header Section */}
        <div className="flex items-center gap-4">
          {companyInformation?.companyInformation.logoUrl && (
            <img
              alt={`${company.name} logo`}
              className="h-20 w-20"
              src={companyInformation.companyInformation.logoUrl}
            />
          )}
          <div className="flex w-full flex-col gap-1">
            <p className="text-2xl font-semibold text-slate-700">
              {company.name}
            </p>
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormControl>
                    <Input placeholder="Address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        {/* About Section */}
        <div className="flex flex-col gap-4">
          <CompanyProfileSectionTitle title="About" />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormControl>
                  <Textarea placeholder="About" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* NAICS Codes Section */}
        <div className="flex flex-col gap-2">
          <CompanyProfileSectionTitle title="NAICS Code(s)" />
          <FormField
            control={form.control}
            name="naicsCodes"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormControl>
                  <ArrayInput field={field} placeholder="NAICS Code(s)" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* Customer Industries Section */}
        <div className="flex flex-col gap-2">
          <CompanyProfileSectionTitle title="Industries of Our Customers" />
          <FormField
            control={form.control}
            name="customerIndustries"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormControl>
                  <ArrayInput field={field} placeholder="Industries" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Manufacturing Capabilities Section */}
        <div className="flex flex-col gap-2">
          <CompanyProfileSectionTitle title="Manufacturing Capabilities / Products" />
          <FormField
            control={form.control}
            name="manufacturingCapabilities"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormControl>
                  <ArrayInput field={field} placeholder="Capabilities" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* Equipment Section */}
        <div className="flex flex-col gap-2">
          <CompanyProfileSectionTitle title="Equipment" />
          <FormField
            control={form.control}
            name="equipment"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormControl>
                  <ArrayInput field={field} placeholder="Equipment" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* Materials Section */}
        <div className="flex flex-col gap-2">
          <CompanyProfileSectionTitle title="Materials" />
          <FormField
            control={form.control}
            name="materials"
            render={({ field }) => (
              <FormItem className="text-slate-700">
                <FormControl>
                  <ArrayInput field={field} placeholder="Materials" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* Company Details Section */}
        <div className="flex flex-col gap-2">
          <CompanyProfileSectionTitle title="Company Details" />
          <div className="grid grid-cols-3 gap-4">
            {/* Headcount */}
            <FormField
              control={form.control}
              name="headcount"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Headcount</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="Headcount"
                      {...field}
                      // TODO: Figure out the best way to handle number inputs.
                      onChange={(e) => {
                        field.onChange(
                          e.target.value ? parseInt(e.target.value, 10) : null,
                        );
                      }}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Founded In */}
            <FormField
              control={form.control}
              name="foundedYear"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Founded In</FormLabel>
                  <FormControl>
                    <Input placeholder="Founded In" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Location */}
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Knoxville, TN" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* D-U-N-S Number */}
            <FormField
              control={form.control}
              name="dunsNumber"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>D-U-N-S Number</FormLabel>
                  <FormControl>
                    <Input placeholder="D-U-N-S Number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* CAGE Code */}
            <FormField
              control={form.control}
              name="cageCode"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>CAGE Code</FormLabel>
                  <FormControl>
                    <Input placeholder="CAGE Code" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            onClick={() => {
              setIsEditing(false);
            }}
            type="button"
            variant="ghost"
          >
            Cancel
          </Button>
          <Button type="submit">Save Changes</Button>
        </div>
      </form>
    </Form>
  );
};
